import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

// Base audit log schema
export const AuditLogSchema = z.object({
  auditLogId: PrimaryKeySchema,
  teamId: PrimaryKeySchema.nullable(),
  userId: PrimaryKeySchema.nullable(),
  apiTokenId: PrimaryKeySchema.nullable(),
  sessionId: PrimaryKeySchema.nullable(),
  eventType: z.string().min(1).max(100),
  resourceType: z.string().min(1).max(50),
  resourceId: PrimaryKeySchema,
  action: z.enum([
    'create',
    'read',
    'update',
    'delete',
    'execute',
    'export',
    'import',
  ]),
  changes: z.record(z.unknown()).nullable(),
  metadata: z
    .object({
      ipAddress: z.string().nullable().optional(),
      userAgent: z.string().nullable().optional(),
      correlationId: z.string().optional(),
      requestId: z.string().optional(),
      additionalInfo: z.record(z.unknown()).optional(),
    })
    .passthrough()
    .nullable(),
  createdAt: z.coerce.date(),
});

export type AuditLog = z.infer<typeof AuditLogSchema>;

export const AuditLogsSchema = z.array(AuditLogSchema);

export type AuditLogs = z.infer<typeof AuditLogsSchema>;

// Create audit log request schema
export const CreateAuditLogRequestSchema = AuditLogSchema.omit({
  auditLogId: true,
  createdAt: true,
});

export type CreateAuditLogRequest = z.infer<typeof CreateAuditLogRequestSchema>;

// Audit log filters schema
export const AuditLogFiltersSchema = createOffsetPaginationRequestQuerySchema(
  z.enum(['createdAt', 'eventType', 'resourceType', 'action']),
)
  .extend({
    teamId: PrimaryKeySchema.optional(),
    userId: PrimaryKeySchema.optional(),
    apiTokenId: PrimaryKeySchema.optional(),
    resourceType: z.string().optional(),
    resourceId: PrimaryKeySchema.optional(),
    eventType: z.string().optional(),
    action: z
      .enum([
        'create',
        'read',
        'update',
        'delete',
        'execute',
        'export',
        'import',
      ])
      .optional(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
    userIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    apiTokenIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    resourceIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  })
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        return data.startDate <= data.endDate;
      }
      return true;
    },
    {
      message: 'Start date must be before or equal to end date',
      path: ['startDate'],
    },
  );

export type AuditLogFilters = z.infer<typeof AuditLogFiltersSchema>;

// Audit log statistics schema
export const AuditLogStatsSchema = z.object({
  totalEvents: z.number(),
  eventsByType: z.record(z.string(), z.number()),
  eventsByAction: z.record(z.string(), z.number()),
  eventsByResource: z.record(z.string(), z.number()),
  eventsByUser: z.record(z.string(), z.number()).optional(),
  eventsOverTime: z
    .array(
      z.object({
        date: z.coerce.date(),
        count: z.number(),
      }),
    )
    .optional(),
});

export type AuditLogStats = z.infer<typeof AuditLogStatsSchema>;

// Audit log retention policy schema
export const AuditLogRetentionPolicySchema = z.object({
  retentionPolicyId: PrimaryKeySchema,
  teamId: PrimaryKeySchema,
  resourceType: z.string().max(50).nullable(),
  retentionDays: z.number().int().positive(),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type AuditLogRetentionPolicy = z.infer<
  typeof AuditLogRetentionPolicySchema
>;

// Create retention policy request schema
export const CreateAuditLogRetentionPolicyRequestSchema =
  AuditLogRetentionPolicySchema.omit({
    retentionPolicyId: true,
    isActive: true,
    createdAt: true,
    updatedAt: true,
  });

export type CreateAuditLogRetentionPolicyRequest = z.infer<
  typeof CreateAuditLogRetentionPolicyRequestSchema
>;

// Update retention policy request schema
export const UpdateAuditLogRetentionPolicyRequestSchema =
  CreateAuditLogRetentionPolicyRequestSchema.partial().extend({
    isActive: z.boolean().optional(),
  });

export type UpdateAuditLogRetentionPolicyRequest = z.infer<
  typeof UpdateAuditLogRetentionPolicyRequestSchema
>;
